import { gql } from '@apollo/client';

import { event_clicked } from '@/analytics';

import { TrackClickEvent_EventFragment } from './graphql-types/TrackClickEvent_EventFragment';

export const trackClickEvent = (event: TrackClickEvent_EventFragment): void => {
  const eventStartDate = event.startDate ?? new Date().toISOString();
  if (event.__typename === 'ConferenceEventV2') {
    const eventRole = !event.currentValidUserRegistration
      ? 'not-registered'
      : event.speakersWithSessions
          ?.map((speaker) => speaker.speaker._id)
          .includes(event.currentValidUserRegistration.attendeeId)
      ? 'speaker'
      : 'attendee';

    event_clicked({
      entity: {
        id: event._id,
        type: 'event',
      },
      event: {
        id: event._id,
        type: 'conference',
        name: event.name,
        startDate: new Date(eventStartDate).toISOString(),
        endDate: event.endDate
          ? new Date(event.endDate).toISOString()
          : undefined,
        role: eventRole,
        isSponsored: false,
      },
      sponsor: {
        hasFollowed: false, // conference events cannot be hosted by sponsor
      },
    });
  } else if (event.__typename === 'SimpleEventV2') {
    const isCourseWorkshop = Boolean(
      (event as TrackClickEvent_EventFragment & { isCourseWorkshop?: boolean })
        .isCourseWorkshop
    );
    // XPRO user journey: member clicked an XPro conference vs a simple/learning event
    const isXproConference = Boolean(event.isXproConferenceEvent);
    const eventType = isXproConference
      ? 'xpro_conference'
      : isCourseWorkshop
      ? 'course_workshop'
      : 'simple';
    event_clicked({
      entity: {
        id: event._id,
        type: isCourseWorkshop ? 'course_workshop' : 'event',
      },
      event: {
        id: event._id,
        type: eventType,
        name: event.name,
        startDate: new Date(eventStartDate).toISOString(),
        endDate: event.endDate
          ? new Date(event.endDate).toISOString()
          : undefined,
        isSponsored: !!event.hostOrganization?._id,
      },
      sponsor: {
        id: event.hostOrganization?._id,
        name: event.hostOrganization?.name,
        hasFollowed: !!event.hostOrganization?._id,
      },
      learning_provider: isCourseWorkshop ? 'intuto' : undefined,
      xpro_event_id: isXproConference ? event.xproEventId ?? undefined : undefined,
      registration_provider: isXproConference ? 'xpro' : undefined,
    });
  }
};

trackClickEvent.eventFragment = gql`
  fragment TrackClickEvent_EventFragment on EventV2 {
    _id
    name
    startDate
    endDate

    currentValidUserRegistration {
      _id
      attendeeId
    }

    ... on SimpleEventV2 {
      _id
      isCourseWorkshop
      isXproConferenceEvent
      xproEventId
      hostOrganization {
        _id
        name
      }
    }
    ... on ConferenceEventV2 {
      _id
      speakersWithSessions {
        speaker {
          _id
        }
      }
    }
  }
`;
